import React, { useState, useEffect, useCallback } from 'react';
import Editor from './components/Editor';
import TimerControls from './components/TimerControls';
import SessionTimeDisplay from './components/SessionTimeDisplay';
import TextStatsDisplay from './components/TextStatsDisplay';
import Header from './components/Header';
import Footer from './components/Footer';
import InactivityBar from './components/InactivityBar';
import Notification, { NotificationType } from './components/Notification';
import SettingsModal from './components/SettingsModal';
import PostSessionModal from './components/PostSessionModal';
import Modal from './components/Modal';
import SavedSessionsList from './components/SavedSessionsList';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import LandingPage from './components/LandingPage';
import FontControls from './components/FontControls';
import StatisticsPage from './components/StatisticsPage';
import AuthModal from './components/AuthModal';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import { useSessionTimer } from './hooks/useSessionTimer';
import { useTextStats } from './hooks/useTextStats';
import { useTheme, Theme } from './hooks/useTheme';
import { useDeviceDetect } from './hooks/useDeviceDetect';
import { useAuth } from './hooks/useAuth';
import { StorageServiceFactory } from './services/storageServiceFactory';
import { SupabaseStorageProvider } from './services/supabaseStorageProvider';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './styles/animations.css';
import './styles/cyberpunk-theme.css';
import './styles/mobile.css';


interface NotificationState {
  message: string;
  type: NotificationType;
  isVisible: boolean;
  id: number;
}

function App() {
  // Theme management
  const { theme, toggleTheme, setThemeDirectly } = useTheme();
  
  // Device detection
  const { isMobile } = useDeviceDetect();
  
  // Authentication
  const { user, signUp, signIn, signOut } = useAuth();
  
  // Enhanced statistics service with cloud sync
  const [statsService] = useState(() => {
    const service = StorageServiceFactory.getStatisticsService(user ? { id: user.id, email: user.email } : null);
    return service;
  });
  
  // Update stats service when user changes
  useEffect(() => {
    if (user) {
      console.log('👤 User authentication detected:', {
        userId: user.id,
        email: user.email,
        timestamp: new Date().toISOString()
      });
      
      // User signed in - connect Supabase storage
      const supabaseProvider = new SupabaseStorageProvider(user.id);
      console.log('🔧 Created SupabaseStorageProvider for user:', user.id);
      
      statsService.setUser({ id: user.id, email: user.email });
      console.log('✅ Updated statsService with user context');
      
      // Connect Supabase to hybrid storage
      const hybridStorage = (statsService as any).storage;
      if (hybridStorage && hybridStorage.setSupabaseProvider) {
        hybridStorage.setSupabaseProvider(supabaseProvider);
        console.log('🔌 Connected Supabase provider to hybrid storage');
        
        // Trigger data sync to merge remote and local sessions
        hybridStorage.syncRemoteToLocal().then((success: boolean) => {
          if (success) {
            console.log('✅ Successfully merged sessions from all devices');
          } else {
            console.warn('⚠️ Session sync completed with warnings');
          }
        }).catch((error: any) => {
          console.error('❌ Failed to sync sessions:', error);
        });
      } else {
        console.error('❌ Failed to access hybrid storage or setSupabaseProvider method');
      }
      
      console.log('🚀 Cloud sync setup complete for user:', user.email);
      
      // Expose debug functions globally for troubleshooting
      (window as any).getDeletetoDebugInfo = () => {
        const hybridStorage = (statsService as any).storage;
        if (hybridStorage && hybridStorage.getDebugInfo) {
          return hybridStorage.getDebugInfo();
        }
        return { error: 'Debug info not available' };
      };
      
      (window as any).resetDeletetoSync = () => {
        const hybridStorage = (statsService as any).storage;
        if (hybridStorage && hybridStorage.resetSyncStatus) {
          hybridStorage.resetSyncStatus();
          return { success: 'Sync status reset' };
        }
        return { error: 'Reset function not available' };
      };
      
      (window as any).forceDeletetoSync = async () => {
        const hybridStorage = (statsService as any).storage;
        if (hybridStorage && hybridStorage.forceSyncRemoteToLocal) {
          const result = await hybridStorage.forceSyncRemoteToLocal();
          return { success: result, message: result ? 'Sync completed' : 'Sync failed' };
        }
        return { error: 'Force sync function not available' };
      };
      
      (window as any).testSupabaseAuth = async () => {
        console.log('🧪 Testing Supabase authentication with timeout...');
        try {
          const supabaseModule = await import('./services/supabaseClient');
          
          // Add 10 second timeout
          const authPromise = supabaseModule.supabase.auth.getUser();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Authentication test timed out after 10 seconds')), 10000)
          );
          
          const { data: authUser, error } = await Promise.race([authPromise, timeoutPromise]) as any;
          
          console.log('Auth test result:', { 
            hasUser: !!authUser?.user, 
            userId: authUser?.user?.id, 
            email: authUser?.user?.email,
            error: error?.message 
          });
          return { authUser, error };
        } catch (e: unknown) {
          const error = e as Error;
          console.error('Auth test failed:', error);
          return { error: error.message || String(e) };
        }
      };
      
      (window as any).testSupabaseConnectivity = async () => {
        console.log('🌐 Testing basic Supabase connectivity...');
        try {
          // Get environment variables directly
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
          const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
          
          if (!supabaseUrl || !supabaseKey) {
            throw new Error('Supabase environment variables not configured');
          }
          
          console.log('Supabase URL:', supabaseUrl);
          
          // Test basic network connectivity to Supabase
          const response = await fetch(supabaseUrl + '/rest/v1/', {
            method: 'HEAD',
            headers: {
              'apikey': supabaseKey
            }
          });
          
          console.log('Connectivity test result:', {
            status: response.status,
            ok: response.ok,
            statusText: response.statusText
          });
          
          return { 
            connected: response.ok, 
            status: response.status,
            url: supabaseUrl 
          };
        } catch (e: unknown) {
          const error = e as Error;
          console.error('Connectivity test failed:', error);
          return { 
            connected: false, 
            error: error.message || String(e)
          };
        }
      };
      
      // Expose supabase for debugging
      import('./services/supabaseClient').then(({ supabase }) => {
        (window as any).supabase = supabase;
        
        // Add direct table test function
        (window as any).testSupabaseTable = async () => {
          console.log('🧪 Testing direct Supabase table access...');
          try {
            const userId = user?.id;
            if (!userId) {
              return { error: 'No user ID available' };
            }
            
            console.log(`Testing table access for user: ${userId}`);
            
            // Test simple select
            const { data, error, count } = await supabase
              .from('user_sessions')
              .select('*', { count: 'exact' })
              .eq('user_id', userId)
              .limit(1);
              
            console.log('Table test result:', { 
              count, 
              hasData: !!data, 
              dataLength: data?.length,
              error: error?.message,
              errorCode: error?.code
            });
            
            return { data, error, count };
          } catch (e: unknown) {
            const error = e as Error;
            console.error('Table test failed:', error);
            return { error: error.message || String(e) };
          }
        };
      });
    } else {
      // User signed out - use localStorage only
      statsService.setUser(null);
      console.log('📱 User signed out - using localStorage only');
      
      // Remove debug functions when signed out
      delete (window as any).getDeletetoDebugInfo;
      delete (window as any).resetDeletetoSync;
      delete (window as any).forceDeletetoSync;
      delete (window as any).testSupabaseAuth;
      delete (window as any).testSupabaseConnectivity;
      delete (window as any).testSupabaseTable;
      delete (window as any).supabase;
    }
  }, [user, statsService]);
  
  // Routing state
  const [currentRoute, setCurrentRoute] = useState<string>(() => {
    return window.location.hash.replace('#', '') || '/';
  });
  
  // State management
  const [text, setText] = useState<string>('');
  const [isSessionActive, setIsSessionActive] = useState<boolean>(false);
  const [filename, setFilename] = useState<string>('untitled');
  const [durationSelection, setDurationSelection] = useState<number>(15);
  const [wordGoalMode, setWordGoalMode] = useState<boolean>(false);
  const [wordGoal, setWordGoal] = useState<number>(500);
  const [isDemoMode, setIsDemoMode] = useState<boolean>(false);
  const [sessionDeletionCount, setSessionDeletionCount] = useState<number>(0);
  const [numTimesDeleto, setNumTimesDeleto] = useState<number>(0);
  
  // Font state
  const [fontFamily, setFontFamily] = useState<string>(() => {
    // Use system fonts on mobile, Open Sans on desktop
    return isMobile ? 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' : 'Open Sans';
  });
  const [fontSize, setFontSize] = useState<number>(() => {
    // Use slightly larger font on mobile for better readability
    return isMobile ? 18 : 16;
  });
  
  // Settings state
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [inactivityTimeout, setInactivityTimeout] = useState<number>(6);
  
  // Memoized close handler for settings modal
  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);
  const [hideInactivitySeconds, setHideInactivitySeconds] = useState<boolean>(false);
  
  // Notification system
  const [notification, setNotification] = useState<NotificationState>({
    message: '',
    type: 'info',
    isVisible: false,
    id: 0
  });
  
  // Add new state for bottom sheet - default expanded
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState<boolean>(true);
  
  // Post session modal state
  const [showPostSessionModal, setShowPostSessionModal] = useState<boolean>(false);
  const [postSessionText, setPostSessionText] = useState<string>('');
  const [finalWordCountForModal, setFinalWordCountForModal] = useState<number>(0);
  
  // State for Saved Sessions Modal
  const [showSessionsModal, setShowSessionsModal] = useState<boolean>(false);
  
  // State for Auth Modal
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  
  // Memoized handlers for Auth Modal
  const handleOpenAuthModal = useCallback(() => {
    setShowAuthModal(true);
  }, []);
  
  const handleCloseAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);
  
  // New state for 'HOW IT WORKS' section
  const [isHowItWorksExpanded, setIsHowItWorksExpanded] = useState<boolean>(true);
  
  // Total words saved from all sessions
  const [totalWordsSaved, setTotalWordsSaved] = useState<number>(0);
  
  // Custom hooks
  const { 
    wordCount, 
    charCount, 
    wordsPerMinute, 
    resetStats,
    deletedChars,
    deletedWords,
    addInactivityDeletion
  } = useTextStats(text);
  
  const { resetInactivity, inactivityProgress, isWarning, isInitialDelayComplete, isCountingDown } = useInactivityTimer({
    isActive: isSessionActive,
    inactivityTimeout,
    initialDelay: 1000,
    visualBreathingRoom: 250,
    onInactivityLimitReached: () => {
      if (text.trim()) {
        if (isDemoMode) {
          showNotification('Demo mode: Text would be deleted here in real mode!', 'warning');
          resetInactivity();
        } else {
          const backupText = text;
          setText('');
          setSessionDeletionCount(prevCount => prevCount + 1);
          setNumTimesDeleto(prev => prev + 1); // Increment deleto counter
          
          // Track this as an inactivity deletion
          addInactivityDeletion(backupText);
          
          showNotification('Your text was deleted due to inactivity!', 'warning');
          
          // DO NOT save the text content for privacy - only save metadata for failed sessions
          const newSession = {
            id: Date.now(),
            date: new Date().toISOString(),
            wordCount,
            charCount,
            sessionDuration: elapsedTime,
            deletedChars,
            deletedWords,
            numTimesDeleto,
           
          };
          statsService.addSession(newSession).catch(error => {
            console.error('Error saving failed session metadata:', error);
          });
        }
      }
    }
  });
  
  // Show notification
  const showNotification = (message: string, type: NotificationType) => {
    // Generate a unique ID for this notification
    const id = Date.now();
    
    setNotification({
      message,
      type,
      isVisible: true,
      id
    });
    
    // Auto-close after 5 seconds (handled by Notification component)
  };
  
  const { timeRemaining, startSession, stopSession, sessionComplete, elapsedTime } = useSessionTimer();
  
  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedTimeout = localStorage.getItem('inactivityTimeout');
    if (savedTimeout) {
      setInactivityTimeout(parseInt(savedTimeout, 10));
    }
    
    const savedHideSeconds = localStorage.getItem('hideInactivitySeconds');
    if (savedHideSeconds) {
      setHideInactivitySeconds(savedHideSeconds === 'true');
    }
    
    // Clean up old mobile mode setting since we removed this feature
    localStorage.removeItem('mobileMode');
  }, []);
  
  // Save inactivity timeout to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('inactivityTimeout', inactivityTimeout.toString());
  }, [inactivityTimeout]);
  
  // Save hideInactivitySeconds to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('hideInactivitySeconds', hideInactivitySeconds.toString());
  }, [hideInactivitySeconds]);
  
  // Fetch total words saved from all sessions
  const fetchTotalWordsSaved = useCallback(async () => {
    try {
      const sessions = await statsService.getSessions();
      const total = sessions.reduce((sum, session) => sum + (session.wordCount || 0), 0);
      setTotalWordsSaved(total);
    } catch (error) {
      console.error('Error fetching total words saved:', error);
    }
  }, [statsService]);
  
  // Load total words on mount and when user changes
  useEffect(() => {
    fetchTotalWordsSaved();
  }, [fetchTotalWordsSaved, user]);
  
  // Handle changing the theme - memoized to prevent unnecessary re-renders
  const handleThemeChange = useCallback((newTheme: Theme) => {
    setThemeDirectly(newTheme);
  }, [setThemeDirectly]);
  
  // Handle text changes
  const handleTextChange = (newText: string) => {
    if (!isSessionActive) return;
    
    setText(newText);
    resetInactivity();
  };
  
  // Start a new session
  const handleStartSession = (duration: number, isWordGoal: boolean, goalTarget?: number) => {
    if (isSessionActive) return;
    
    // Always clear text when starting a new session
    setText('');
    
    // Set session parameters
    setDurationSelection(duration);
    setWordGoalMode(isWordGoal);
    if (isWordGoal && goalTarget) {
      setWordGoal(goalTarget);
    }
    
    // Start timers
    setSessionDeletionCount(0);
    setNumTimesDeleto(0); // Reset deleto counter for new session
    startSession(duration);
    resetInactivity();
    resetStats(); // Reset deletion and timing tracking for new session
    setIsSessionActive(true);
    
    // Collapse the bottom sheet when starting a session on mobile
    if (isMobile) {
      setIsBottomSheetExpanded(false);
    }
  };
  
  // Reset the session
  const handleResetSession = () => {
    // For early session end - don't save text content for privacy
    if (isSessionActive) {
      const adjustedNumTimesDeleto = (numTimesDeleto || 0) + 1;
      const newSession = {
        id: Date.now(),
        date: new Date().toISOString(),
        wordCount,
        charCount,
        sessionDuration: elapsedTime,
        deletedChars,
        deletedWords,
        numTimesDeleto: adjustedNumTimesDeleto,
        sessionEndedEarly: true // Mark as ended early
      };
      statsService.addSession(newSession).catch(error => {
        console.error('Error saving early session end metadata:', error);
      });
    }
    handleSessionEnd();
  };
  
  // Load text from a saved session
  const handleLoadText = async (sessionId: number) => {
    try {
      const session = await statsService.getSessionById(sessionId);
      
      if (session) {
        // Note: Sessions no longer contain text content for privacy
        // This function now only loads session metadata
        showNotification('Session data loaded (text content not saved for privacy)', 'info');
      } else {
        showNotification('Session not found', 'error');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      showNotification('Failed to load session', 'error');
    }
  };
  
  // Delete a saved session
  const handleDeleteSession = async (sessionId: number) => {
    try {
      const success = await statsService.deleteSession(sessionId);
      
      if (success) {
        showNotification('Session deleted', 'info');
      } else {
        showNotification('Failed to delete session', 'error');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      showNotification('Failed to delete session', 'error');
    }
  };
  
  // Reset editor (different from resetting a session - this can be used outside of active sessions)
  const handleResetEditor = () => {
    if (text.trim() && !isSessionActive) {
      if (window.confirm('Clear the editor? Your current text will be lost.')) {
        setText('');
        setFilename('untitled');
        showNotification('Editor cleared', 'info');
      }
    } else if (!isSessionActive) {
      setText('');
      setFilename('untitled');
    }
  };
  
  // Close notification
  const closeNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      isVisible: false
    }));
    
    // Return focus to the editor textarea
    setTimeout(() => {
      const textArea = document.querySelector('.editor-container textarea') as HTMLTextAreaElement;
      if (textArea && isSessionActive) {
        textArea.focus();
      }
    }, 10);
  }, [isSessionActive]);
  
  // Warn before leaving if there's unsaved text
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (text.trim()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [text]);
  
  // DEBUG: Log sessionComplete changes
  useEffect(() => {
    console.log('[DEBUG] sessionComplete changed to:', sessionComplete);
  }, [sessionComplete]);
  
  // Handle word goal completion - when word count reaches goal, complete the session
  useEffect(() => {
    if (wordGoalMode && wordCount >= wordGoal && isSessionActive) {
      console.log('[DEBUG] Word goal reached! Completing session.');
      
      // Save session data
      setFinalWordCountForModal(wordCount);
      setIsSessionActive(false);
      stopSession();
      
      const newSession = {
        id: Date.now(),
        date: new Date().toISOString(),
        wordCount,
        charCount,
        sessionDuration: elapsedTime,
        deletedChars,
        deletedWords,
        numTimesDeleto,
        sessionCompleted: true
      };
      
      statsService.addSession(newSession).then(() => {
        console.log('Word goal session completed and saved.');
        fetchTotalWordsSaved();
      }).catch(error => {
        console.error('Error saving word goal session:', error);
      });
      
      // Show the post session modal
      setPostSessionText(text);
      setShowPostSessionModal(true);
      showNotification('Word goal reached! Great job! 🎉', 'success');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordGoalMode, wordCount, wordGoal, isSessionActive]);
  
  // Handle session completion
  useEffect(() => {
    if (sessionComplete && isSessionActive) {
      setFinalWordCountForModal(wordCount);
      setIsSessionActive(false);
      // Only save session metadata on completion - NO TEXT CONTENT
      const newSession = {
        id: Date.now(),
        date: new Date().toISOString(),
        wordCount,
        charCount,
        sessionDuration: elapsedTime,
        deletedChars,
        deletedWords,
        numTimesDeleto,
        sessionCompleted: true
      };
      
      statsService.addSession(newSession).then(() => {
        console.log('Session completion metadata saved (no text content).');
        fetchTotalWordsSaved(); // Refresh total words count
      }).catch(error => {
        console.error('Error saving session completion metadata:', error);
      });

      // Prepare and show the post session modal
      setPostSessionText(text);
      setShowPostSessionModal(true);
      console.log('Attempting to show PostSessionModal. State showPostSessionModal should be true.');
      showNotification('Session complete! Choose an option below.', 'success');
    } else {
      if (sessionComplete && !isSessionActive) {
        console.log('Session complete effect: sessionComplete is true, but isSessionActive is false. Modal not shown because isSessionActive was already false.');
      } else if (!sessionComplete) {
        // console.log('Session complete effect: sessionComplete is false. Modal not shown.'); // Optional: too noisy if checked frequently
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionComplete, isSessionActive, text, wordCount, charCount, durationSelection, sessionDeletionCount, elapsedTime, deletedChars, deletedWords, numTimesDeleto, fetchTotalWordsSaved]);
  
  // Toggle bottom sheet function - maintain text area focus
  const toggleBottomSheet = () => {
    // Save the current focus state
    const textArea = document.querySelector('.editor-container textarea') as HTMLTextAreaElement;
    const wasTextAreaFocused = document.activeElement === textArea;
    
    // Toggle the bottom sheet
    setIsBottomSheetExpanded(!isBottomSheetExpanded);
    
    // Return focus to text area if it was focused before and a session is active
    if (wasTextAreaFocused && isSessionActive) {
      setTimeout(() => {
        if (textArea) {
          textArea.focus();
        }
      }, 10);
    }
  };
  
  // Handle session completion
  const handleSessionEnd = () => {
    // If there was a session active, do not save the text
    if (isSessionActive) {
      setIsSessionActive(false);
      setText(''); 
      showNotification('User ended Session. Text is not saved.', 'info');
    }
  };
  
  // Handle routing
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentRoute(window.location.hash.replace('#', '') || '/');
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  
  // Handle changing the hide seconds setting - memoized to prevent unnecessary re-renders
  const handleHideInactivitySecondsChange = useCallback((value: boolean) => {
    setHideInactivitySeconds(value);
  }, []);

  // Handle changing the inactivity timeout - memoized to prevent unnecessary re-renders
  const handleInactivityTimeoutChange = useCallback((value: number) => {
    setInactivityTimeout(value);
  }, []);

  // Navigation handlers
  const handleGetStarted = () => {
    setIsDemoMode(false);
    setCurrentRoute('/');
    window.location.hash = '#/';
    // Mark that user has visited the main app
    localStorage.setItem('hasVisitedBefore', 'true');
    // Automatically start a 3-minute session
    handleStartSession(3, false);
  };

  const handleTryDemo = () => {
    setIsDemoMode(true);
    setCurrentRoute('/demo');
    window.location.hash = '#/demo';
  };
  
  // Handlers for the PostSessionModal
  const handlePostSessionSave = async (customFilename: string, sessionTextToSave: string, format: string) => {
    const finalFilename = customFilename || 'untitled';
    try {
      // DO NOT save text to localStorage - only trigger download
      // This preserves the core concept of Get Deleto - text disappears unless explicitly saved

      const selected = (format || localStorage.getItem('lastExportFormat') || 'txt').toLowerCase();
      let downloadFileName = `${finalFilename}.${selected}`;

      if (selected === 'txt' || selected === 'md') {
        const mime = selected === 'md' ? 'text/markdown' : 'text/plain';
        const blob = new Blob([sessionTextToSave], { type: mime });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (selected === 'docx') {
        const { Document, Packer, Paragraph } = await import('docx');
        const paragraphs = sessionTextToSave.split('\n').map(line => new Paragraph({ text: line }));
        const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (selected === 'pdf') {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ unit: 'pt', format: 'letter' });
        const left = 40;
        const top = 40;
        const lineHeight = 16;
        const maxWidth = 522; // ~ 8.5in - margins
        const lines = doc.splitTextToSize(sessionTextToSave || '', maxWidth);
        let y = top;
        lines.forEach((line: string) => {
          if (y > 750) { // simple page break
            doc.addPage();
            y = top;
          }
          doc.text(line, left, y);
          y += lineHeight;
        });
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Fallback to txt
        downloadFileName = `${finalFilename}.txt`;
        const blob = new Blob([sessionTextToSave], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      showNotification(`Downloaded as ${downloadFileName}`, 'success');
      setFilename(finalFilename); // Update app's main filename state
    } catch (error) {
      console.error('Error in handlePostSessionSave:', error);
      showNotification('Failed to save session', 'error');
    }
    // Removed setShowPostSessionModal(false) so modal stays open for multiple actions
  };

  const handlePostSessionCopy = (sessionTextToCopy: string) => {
    navigator.clipboard.writeText(sessionTextToCopy)
      .then(() => {
        showNotification('Text copied to clipboard!', 'success');
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        showNotification('Failed to copy text', 'error');
      });
    // Keep the modal open or close it based on preference. For now, let's keep it open.
  };
  
  const handleClosePostSessionModal = () => {
    setShowPostSessionModal(false);
    // Optionally clear the text from the editor if it's no longer needed
    // setText(''); 
  };
  
  // Handler to open the Session Statistics Modal
  const handleOpenSessionsModal = () => {
    setShowSessionsModal(true);
  };

  // Handler to close the Saved Sessions Modal (can also be inline in JSX)
  const handleCloseSessionsModal = () => {
    setShowSessionsModal(false);
  };

  // Handler to navigate to statistics page
  const handleOpenStatistics = () => {
    setCurrentRoute('/statistics');
    window.location.hash = '#/statistics';
  };

  return (
    <>
      {/* Landing Page - Intro Route */}
      {currentRoute === '/intro' && (
        <LandingPage
          onGetStarted={handleGetStarted}
          onTryDemo={handleTryDemo}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
      )}

      {/* Main App Routes - Root and Demo */}
      {(currentRoute === '/' || currentRoute === '/demo') && (
        <div className={`min-h-screen flex flex-col bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-slate-50 ${isMobile ? 'mobile' : ''}`}>
          {/* Demo Mode Banner */}
          {isDemoMode && (
            <div className="bg-yellow-500 text-black px-4 py-2 text-center font-medium">
              🚀 Demo Mode: Text won't actually be deleted - this is just for testing!
              <a href="#/intro" className="ml-4 underline hover:no-underline">← Back to Intro</a>
            </div>
          )}
          
          <Header 
            filename={filename}
            onFilenameChange={setFilename}
            isSessionActive={isSessionActive}
            onNewSession={handleResetEditor}
            onLoadText={handleLoadText}
            onDeleteSession={handleDeleteSession}
            onResetEditor={handleResetEditor}
            theme={theme}
            onToggleTheme={toggleTheme}
            onSettingsClick={() => setShowSettings(true)}
            onOpenSessionsClick={handleOpenSessionsModal}
            onStatisticsClick={handleOpenStatistics}
            onAccountClick={handleOpenAuthModal}
            user={user}
            totalWordsSaved={totalWordsSaved}
          />
          
          <main className={`flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row gap-6 animate-fade-in container ${isMobile && isSessionActive ? 'session-active' : ''}`}>
            <div className="w-full md:w-[75%] flex flex-col shadow-lg rounded-lg overflow-hidden bg-white dark:bg-gray-800 transition-all duration-200">
              {/* Mobile inactivity bar at the top */}
              {isSessionActive && isMobile && isInitialDelayComplete && (
                <div className={`mobile-inactivity-container ${isWarning ? 'warning' : ''}`}>
                  <div className="mobile-inactivity-text">
                    {isWarning ? 
                      `Keep typing!${hideInactivitySeconds ? '' : ` (${Math.max(0, Math.ceil(inactivityTimeout - (inactivityTimeout * inactivityProgress / 100)))}s)`}` : 
                      `${hideInactivitySeconds ? 'Inactivity' : `${Math.max(0, Math.ceil(inactivityTimeout - (inactivityTimeout * inactivityProgress / 100)))}s remaining`}`
                    }
                  </div>
                  <div className="mobile-inactivity-bar-bg">
                    <div 
                      className="mobile-inactivity-bar-fill"
                      style={{
                        width: `${inactivityProgress}%`,
                        backgroundColor: (() => {
                          // Use the same color logic as in InactivityBar
                          if (inactivityProgress < 20) return '#10B981'; // green-500
                          if (inactivityProgress < 40) return '#34D399'; // green-400
                          if (inactivityProgress < 50) return '#FBBF24'; // yellow-400
                          if (inactivityProgress < 60) return '#F59E0B'; // amber-500
                          if (inactivityProgress < 70) return '#F97316'; // orange-500
                          if (inactivityProgress < 80) return '#EF4444'; // red-500
                          return '#DC2626'; // red-600
                        })()
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Font Controls - Desktop only */}
              {!isMobile && (
                <FontControls 
                  fontFamily={fontFamily}
                  onFontFamilyChange={setFontFamily}
                  fontSize={fontSize}
                  onFontSizeChange={setFontSize}
                />
              )}
              
              {/* Desktop inactivity bar - hidden on mobile */}
              {(!isMobile) && (
                <div className="relative w-full">
                  <InactivityBar
                    inactivityProgress={inactivityProgress}
                    isActive={isSessionActive}
                    isWarning={isWarning}
                    isInitialDelayComplete={isInitialDelayComplete}
                    isCountingDown={isCountingDown}
                    inactivityTimeout={inactivityTimeout}
                    hideSeconds={hideInactivitySeconds}
                  />
                </div>
              )}
              
              <Editor 
                value={text}
                onChange={handleTextChange}
                isSessionActive={isSessionActive}
                onStartSession={handleStartSession}
                onResetSession={handleResetSession}
                wordCount={wordCount}
                characterCount={charCount}
                fontFamily={fontFamily}
                fontSize={fontSize}
              />
            </div>
            
            {/* Desktop layout for timer controls */}
            {(!isMobile) && (
              <div className="w-full md:w-[25%] flex flex-col gap-4">
                <div className="p-5 bg-white dark:bg-gray-800 rounded-lg shadow-lg animate-slide-up">
                  {/* Conditionally render Session Time title */}
                  {(isSessionActive) && (
                    <div className="mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">Session Time</h3>
                    </div>
                  )}
                  <SessionTimeDisplay
                    isSessionActive={isSessionActive}
                    sessionDuration={durationSelection}
                    timeRemaining={timeRemaining}
                    wordCount={wordCount}
                    wordGoal={wordGoal}
                    isWordGoalMode={wordGoalMode}
                    onResetSession={handleResetSession}
                    showSessionConfigurator={!isSessionActive}
                    onToggleShowSessionConfigurator={() => {}}
                    onStartSession={handleStartSession}
                  />
                </div>
                
                <TextStatsDisplay 
                  wordCount={wordCount} 
                  charCount={charCount}
                  deletedChars={deletedChars}
                  deletedWords={deletedWords}
                  sessionDuration={elapsedTime}
                  wordsPerMinute={wordsPerMinute}
                />
                
                <div className="p-5 bg-white dark:bg-gray-800 rounded-lg shadow-lg animate-slide-up delay-200">
                  <div 
                    className="flex justify-between items-center mb-4 cursor-pointer group"
                    onClick={() => setIsHowItWorksExpanded(!isHowItWorksExpanded)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary dark:group-hover:text-primary transition-colors" data-text="How it Works">HOW IT WORKS</h3>
                    <svg 
                      className={`w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-primary dark:group-hover:text-primary transition-transform duration-200 ${isHowItWorksExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                  
                  {isHowItWorksExpanded && (
                    <div className="space-y-4 text-sm animate-fade-in-fast">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                          1
                        </div>
                        <div className="text-black dark:text-white leading-relaxed">
                          <span className="font-medium">Start New Session</span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                          2
                        </div>
                        <div className="text-black dark:text-white leading-relaxed">
                          <span className="font-medium">Choose a duration or word-count goal. Press Start Session.</span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                          3
                        </div>
                        <div className="text-black dark:text-white leading-relaxed">
                          <span className="font-medium">Type nonstop. Pause too long and the draft is deleted.</span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                          4
                        </div>
                        <div className="text-black dark:text-white leading-relaxed">
                          <span className="font-medium">Outwrite the timer, then Save, Copy or Share your text.</span>
                        </div>
                      </div>

                      {/* New warning item */}
                      <div className="flex items-start gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                        <div className="text-black dark:text-white leading-relaxed">
                          <span className="font-medium">Not typing fast enough or ending a session early will delete your draft.</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Mobile bottom sheet for timer controls */}
            {(isMobile) && (
              <div 
                className={`timer-bottom-sheet ${isBottomSheetExpanded ? 'expanded' : 'collapsed'} ${isSessionActive ? 'session-active' : ''}`}
              >
                <div className="sheet-header" onClick={toggleBottomSheet}>
                  <div className="sheet-handle"></div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 pt-2">Timer Controls</h3>
                </div>
                <div className="timer-content">
                  <TimerControls
                    onStartSession={handleStartSession}
                    onResetSession={handleResetSession}
                    isSessionActive={isSessionActive}
                    sessionDuration={durationSelection}
                    timeRemaining={timeRemaining}
                    wordCount={wordCount}
                    wordGoal={wordGoal}
                    isWordGoalMode={wordGoalMode}
                  />
                </div>
              </div>
            )}
          </main>
          
          <Footer />
          
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            isVisible={notification.isVisible}
            onClose={closeNotification}
          />
          
          <SettingsModal
            isOpen={showSettings}
            onClose={handleCloseSettings}
            inactivityTimeout={inactivityTimeout}
            onInactivityTimeoutChange={handleInactivityTimeoutChange}
            hideInactivitySeconds={hideInactivitySeconds}
            onHideInactivitySecondsChange={handleHideInactivitySecondsChange}
            theme={theme}
            onThemeChange={handleThemeChange}
          />
        </div>
      )}
      
      {currentRoute === '/privacy' && <PrivacyPolicy />}
      {currentRoute === '/terms' && <TermsOfService />}
      {currentRoute === '/statistics' && <StatisticsPage />}
      
      {/* Post Session Modal */}
      <PostSessionModal
        isOpen={showPostSessionModal}
        onClose={handleClosePostSessionModal}
        sessionText={postSessionText}
        initialFilename={filename}
        onSave={handlePostSessionSave}
        onCopy={handlePostSessionCopy}
        theme={theme}
        wordCountAtCompletion={finalWordCountForModal}
        sessionDurationMinutes={durationSelection}
        deletionCount={sessionDeletionCount}
      />
      
      {/* Session Statistics Modal - Rendered at App level */}
      <Modal 
        isOpen={showSessionsModal} 
        onClose={handleCloseSessionsModal} 
        title="Session History & Statistics"
        size="md"
      >
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            📊 This shows your session statistics only. Text content is never saved to preserve the core concept of Get Deleto.
          </p>
        </div>
        <SavedSessionsList 
          onLoad={(sessionId) => { 
            // Sessions can no longer be loaded since they contain no text
            console.log('Session viewing disabled - no text content saved');
          }} 
          onDelete={handleDeleteSession} 
        />
        <div className="flex justify-end mt-4">
          <button onClick={handleCloseSessionsModal} className="btn btn-secondary">Close</button>
        </div>
      </Modal>
      
      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseAuthModal}
        onSignUp={signUp}
        onSignIn={signIn}
        onSignOut={signOut}
        user={user}
      />
      
      <Analytics />
      <SpeedInsights />
    </>
  );
}

export default App; 